// components/LoginScreen.js - Password protection login screen
import React, { useState } from 'react';
import { RefreshCw, Lock, Eye, EyeOff } from 'lucide-react';

const LoginScreen = ({ onLogin, staticPassword }) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Simulate loading delay for security
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (password === staticPassword) {
            onLogin();
        } else {
            setError('Invalid password. Access denied.');
            setPassword('');
        }

        setLoading(false);
    };

    const loginStyles = {
        container: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        },
        loginBox: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '40px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
            animation: 'fadeIn 0.6s ease-out'
        },
        header: {
            textAlign: 'center',
            marginBottom: '30px'
        },
        icon: {
            width: '64px',
            height: '64px',
            background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 8px 32px rgba(124, 58, 237, 0.3)'
        },
        title: {
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: '600',
            margin: '0 0 8px 0'
        },
        subtitle: {
            color: '#94a3b8',
            fontSize: '0.875rem',
            margin: '0'
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
        },
        inputGroup: {
            position: 'relative'
        },
        input: {
            width: '100%',
            padding: '14px 16px',
            paddingRight: '50px',
            background: 'rgba(15, 23, 42, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
        },
        toggleButton: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            transition: 'color 0.2s ease'
        },
        submitButton: {
            width: '100%',
            padding: '14px',
            background: loading ? '#6b7280' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        },
        error: {
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#fca5a5',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '0.875rem',
            textAlign: 'center'
        },
        footer: {
            textAlign: 'center',
            marginTop: '24px',
            padding: '16px 0',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        },
        footerText: {
            color: '#64748b',
            fontSize: '0.75rem'
        }
    };

    return (
        <div style={loginStyles.container}>
            <div style={loginStyles.loginBox}>
                <div style={loginStyles.header}>
                    <div style={loginStyles.icon}>
                        <Lock size={28} color="white" />
                    </div>
                    <h1 style={loginStyles.title}>Secure Access</h1>
                    <p style={loginStyles.subtitle}>Access Monitoring Dashboard</p>
                </div>

                <form onSubmit={handleSubmit} style={loginStyles.form}>
                    <div style={loginStyles.inputGroup}>
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter access password"
                            style={{
                                ...loginStyles.input,
                                borderColor: error ? 'rgba(239, 68, 68, 0.5)' : 'rgba(255, 255, 255, 0.1)'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                            onBlur={(e) => e.target.style.borderColor = error ? 'rgba(239, 68, 68, 0.5)' : 'rgba(255, 255, 255, 0.1)'}
                            required
                            disabled={loading}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                                ...loginStyles.toggleButton,
                                color: showPassword ? '#7c3aed' : '#94a3b8'
                            }}
                            onMouseOver={(e) => e.target.style.color = '#7c3aed'}
                            onMouseOut={(e) => e.target.style.color = showPassword ? '#7c3aed' : '#94a3b8'}
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    {error && (
                        <div style={loginStyles.error}>
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || !password}
                        style={{
                            ...loginStyles.submitButton,
                            opacity: loading || !password ? 0.7 : 1
                        }}
                        onMouseOver={(e) => {
                            if (!loading && password) {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 8px 25px rgba(124, 58, 237, 0.4)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        {loading ? (
                            <>
                                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                Authenticating...
                            </>
                        ) : (
                            <>
                                <Lock size={16} />
                                Access Dashboard
                            </>
                        )}
                    </button>
                </form>

                <div style={loginStyles.footer}>
                    <p style={loginStyles.footerText}>
                        🔒 Secure access required • Authorized personnel only
                    </p>
                </div>
            </div>

            <style>
                {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          input::placeholder {
            color: #64748b;
          }
        `}
            </style>
        </div>
    );
};

export default LoginScreen;